/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import {
  Users,
  Shapes,
  Combine,
  Split,
  RotateCw,
  Lock,
  Unlock,
  Copy,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  ShieldAlert,
  Check,
} from 'lucide-react';
import { TablePlan, FormeTable, Permissions, ModeleTable } from './types.ts';
import { verifierCompatibiliteCombinaison } from './logiquePure.ts';

interface MenuActionsTableProps {
  table: TablePlan;
  toutesTables: TablePlan[];
  modeles: ModeleTable[];
  permissions: Permissions;
  tablesSelectionneesMulti?: string[];
  estMobile: boolean;
  onFermer: () => void;
  onChangerCapacite: (tableId: string, nouvelleCapacite: number) => void;
  onChangerForme: (tableId: string, nouvelleForme: FormeTable) => void;
  onPivoter: (tableId: string, delta: number) => void;
  onBloquer: (tableId: string, bloquee: boolean, motif?: string) => void;
  onFusionner: (tableIds: string[], capacite?: number) => void;
  onSeparer: (groupeId: string) => void;
  onDupliquer: (tableId: string) => void;
  onRenommer: (tableId: string, nouveauNom: string) => void;
  onSupprimer: (tableId: string) => void;
}

export const MenuActionsTable: React.FC<MenuActionsTableProps> = ({
  table,
  toutesTables,
  modeles,
  permissions,
  tablesSelectionneesMulti = [],
  estMobile,
  onFermer,
  onChangerCapacite,
  onChangerForme,
  onPivoter,
  onBloquer,
  onFusionner,
  onSeparer,
  onDupliquer,
  onRenommer,
  onSupprimer,
}) => {
  const [ongletActif, setOngletActif] = useState<'principal' | 'capacite' | 'forme' | 'bloquer' | 'renommer' | 'fusion' | 'confirmer_suppression'>('principal');
  const [capacitePerso, setCapacitePerso] = useState<string>(String(table.capaciteMax));
  const [nouveauNom, setNouveauNom] = useState<string>(table.nom);
  const [motifBlocage, setMotifBlocage] = useState<string>(table.motifBlocage || '');
  const [tableCibleFusionId, setTableCibleFusionId] = useState<string>('');
  const [erreurCombinaison, setErreurCombinaison] = useState<string | null>(null);

  // Autres tables de la même zone candidates à la fusion
  const tablesCandidatesFusion = toutesTables.filter(
    (t) => t.id !== table.id && t.zoneId === table.zoneId && !t.bloquee
  );

  // Vérification de compatibilité de fusion si plusieurs tables sont sélectionnées
  const tablesSelectionnees =
    tablesSelectionneesMulti.length > 1
      ? toutesTables.filter((t) => tablesSelectionneesMulti.includes(t.id))
      : [table];

  const modelesMap = new Map(modeles.map((m) => [m.id, m]));

  const verifierEtFusionner = (tableIds: string[], capacite?: number) => {
    const tablesAFusionner = toutesTables.filter((t) => tableIds.includes(t.id));
    const compatibilite = verifierCompatibiliteCombinaison(tablesAFusionner, modelesMap);

    if (!compatibilite.compatible) {
      setErreurCombinaison(compatibilite.raison || 'Combinaison non autorisée.');
      return;
    }

    setErreurCombinaison(null);
    onFusionner(tableIds, capacite);
    onFermer();
  };

  const confirmerSuppression = () => {
    onSupprimer(table.id);
    onFermer();
  };

  return (
    <div
      role="dialog"
      aria-labelledby="titre-menu-table"
      aria-modal="true"
      className={`${
        estMobile
          ? 'fixed inset-x-0 bottom-0 z-50 rounded-t-2xl max-h-[85vh] overflow-y-auto shadow-2xl'
          : 'relative rounded-xl shadow-lg max-w-sm w-full'
      } bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 transition-all animate-in fade-in slide-in-from-bottom-4`}
    >
      {/* En-tête avec nom de la table et statut permissions */}
      <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-bold flex items-center justify-center text-sm">
            {table.nom}
          </span>
          <div>
            <h3 id="titre-menu-table" className="font-bold text-sm text-slate-900 dark:text-slate-100">
              Table {table.nom}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
              {table.forme} • {table.capaciteMax} couverts {table.groupeId ? '• Fusionnée' : ''}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onFermer}
          aria-label="Fermer le menu de la table"
          className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Alerte si rôle restreint (sans permission modifierPlan) */}
      {!permissions.modifierPlan && (
        <div className="mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 flex items-start gap-2 text-xs text-amber-900 dark:text-amber-200">
          <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <strong className="block font-semibold">Réservé à la direction</strong>
            Les actions de transformation du plan (capacité, forme, fusion, déplacement) nécessitent les droits de modification.
          </div>
        </div>
      )}

      {/* Message d'erreur de combinaison incompatible */}
      {erreurCombinaison && (
        <div className="mt-3 p-2.5 rounded-lg bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 flex items-start gap-2 text-xs text-red-800 dark:text-red-200">
          <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
          <span>{erreurCombinaison}</span>
        </div>
      )}

      {/* CONTENU SELON ONGLET */}
      <div className="py-3">
        {/* VUE PRINCIPALE DES ACTIONS RAPIDES */}
        {ongletActif === 'principal' && (
          <div className="space-y-2">
            {permissions.modifierPlan ? (
              <>
                {/* 1. Changer la capacité (2, 4, 6, 8, 12, libre) */}
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" /> Capacité :
                  </span>
                  <div className="grid grid-cols-6 gap-1">
                    {[2, 4, 6, 8, 12].map((cap) => (
                      <button
                        key={cap}
                        type="button"
                        onClick={() => {
                          onChangerCapacite(table.id, cap);
                          onFermer();
                        }}
                        aria-label={`Passer la capacité à ${cap} couverts`}
                        className={`py-2 px-1 rounded-md text-xs font-bold min-h-[44px] flex items-center justify-center cursor-pointer transition-colors ${
                          table.capaciteMax === cap
                            ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-xs'
                            : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                        }`}
                      >
                        {cap}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setOngletActif('capacite')}
                      aria-label="Saisir une capacité personnalisée"
                      className="py-2 px-1 rounded-md text-xs font-bold min-h-[44px] flex items-center justify-center bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer"
                    >
                      Autre
                    </button>
                  </div>
                </div>

                {/* 2. Changer la forme */}
                <div className="space-y-1 pt-1">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                    <Shapes className="w-3.5 h-3.5" /> Forme :
                  </span>
                  <div className="grid grid-cols-5 gap-1">
                    {(['ronde', 'carree', 'rectangulaire', 'ovale', 'plateau'] as FormeTable[]).map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => {
                          onChangerForme(table.id, f);
                          onFermer();
                        }}
                        aria-label={`Changer la forme en ${f}`}
                        className={`p-1.5 rounded-md text-[11px] font-semibold capitalize min-h-[44px] flex flex-col items-center justify-center cursor-pointer transition-colors ${
                          table.forme === f
                            ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-xs'
                            : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                        }`}
                      >
                        {f === 'ronde'
                          ? 'Ronde'
                          : f === 'carree'
                          ? 'Carrée'
                          : f === 'rectangulaire'
                          ? 'Rect.'
                          : f === 'ovale'
                          ? 'Ovale'
                          : 'Plateau'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 3. Fusionner / Séparer */}
                <div className="pt-1">
                  {table.groupeId ? (
                    <button
                      type="button"
                      onClick={() => {
                        onSeparer(table.groupeId!);
                        onFermer();
                      }}
                      aria-label="Séparer les tables combinées"
                      className="w-full py-2.5 px-3 rounded-lg bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/60 dark:hover:bg-indigo-900/80 text-indigo-900 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-800 font-semibold text-xs flex items-center justify-center gap-2 min-h-[44px] cursor-pointer"
                    >
                      <Split className="w-4 h-4 text-indigo-600" />
                      <span>Séparer la table fusionnée</span>
                    </button>
                  ) : tablesSelectionneesMulti.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => verifierEtFusionner(tablesSelectionneesMulti)}
                      aria-label={`Fusionner les ${tablesSelectionneesMulti.length} tables sélectionnées`}
                      className="w-full py-2.5 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs flex items-center justify-center gap-2 min-h-[44px] cursor-pointer shadow-xs active:scale-95"
                    >
                      <Combine className="w-4 h-4" />
                      <span>Fusionner ces {tablesSelectionneesMulti.length} tables</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setOngletActif('fusion')}
                      aria-label="Ouvrir la combinaison avec une autre table"
                      className="w-full py-2 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold text-xs flex items-center justify-center gap-2 min-h-[44px] cursor-pointer"
                    >
                      <Combine className="w-4 h-4 text-slate-500" />
                      <span>Coller / Fusionner avec une table...</span>
                    </button>
                  )}
                </div>

                {/* 4. Rotation, Dupliquer, Renommer */}
                <div className="grid grid-cols-3 gap-1.5 pt-1">
                  <button
                    type="button"
                    onClick={() => onPivoter(table.id, 15)}
                    aria-label="Pivoter la table de 15 degrés"
                    className="py-2 px-2 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 min-h-[44px] cursor-pointer"
                  >
                    <RotateCw className="w-4 h-4 text-slate-500" />
                    <span>Pivoter +15°</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onDupliquer(table.id);
                      onFermer();
                    }}
                    aria-label="Dupliquer la table"
                    className="py-2 px-2 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 min-h-[44px] cursor-pointer"
                  >
                    <Copy className="w-4 h-4 text-slate-500" />
                    <span>Dupliquer</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setOngletActif('renommer')}
                    aria-label="Renommer la table"
                    className="py-2 px-2 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 min-h-[44px] cursor-pointer"
                  >
                    <Edit2 className="w-4 h-4 text-slate-500" />
                    <span>Renommer</span>
                  </button>
                </div>

                {/* 5. Bloquer / Supprimer */}
                <div className="grid grid-cols-2 gap-1.5 pt-1">
                  <button
                    type="button"
                    onClick={() => setOngletActif('bloquer')}
                    aria-label={table.bloquee ? 'Débloquer la table' : 'Bloquer la table'}
                    className={`py-2 px-3 rounded-lg font-semibold text-xs flex items-center justify-center gap-2 min-h-[44px] cursor-pointer ${
                      table.bloquee
                        ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200'
                        : 'bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-200'
                    }`}
                  >
                    {table.bloquee ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                    <span>{table.bloquee ? 'Débloquer' : 'Bloquer table'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setOngletActif('confirmer_suppression')}
                    aria-label={`Supprimer la table ${table.nom}`}
                    className="py-2 px-3 rounded-lg bg-red-50 hover:bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 font-semibold text-xs flex items-center justify-center gap-2 min-h-[44px] cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                    <span>Supprimer</span>
                  </button>
                </div>
              </>
            ) : (
              /* Vue réservée au personnel sans droits de modification */
              <div className="space-y-3 py-2">
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  Vous pouvez consulter l’occupation et placer les réservations sur cette table.
                </p>
                {table.bloquee && (
                  <div className="p-2.5 rounded bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-xs">
                    <strong>Table bloquée :</strong> {table.motifBlocage || 'Maintenance en cours'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* SOUS-MENU : CAPACITÉ LIBRE */}
        {ongletActif === 'capacite' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const val = parseInt(capacitePerso, 10);
              if (val > 0) {
                onChangerCapacite(table.id, val);
                onFermer();
              }
            }}
            className="space-y-3"
          >
            <label htmlFor="capacite-libre" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Nombre de couverts (saisie libre) :
            </label>
            <input
              id="capacite-libre"
              type="number"
              min="1"
              max="40"
              value={capacitePerso}
              onChange={(e) => setCapacitePerso(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-base font-bold min-h-[44px]"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOngletActif('principal')}
                className="flex-1 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold min-h-[44px]"
              >
                Retour
              </button>
              <button
                type="submit"
                className="flex-1 py-2 rounded-lg bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 text-xs font-bold min-h-[44px]"
              >
                Valider
              </button>
            </div>
          </form>
        )}

        {/* SOUS-MENU : RENOMMER */}
        {ongletActif === 'renommer' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (nouveauNom.trim()) {
                onRenommer(table.id, nouveauNom.trim());
                onFermer();
              }
            }}
            className="space-y-3"
          >
            <label htmlFor="nouveau-nom" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Nouveau nom ou numéro de la table :
            </label>
            <input
              id="nouveau-nom"
              type="text"
              value={nouveauNom}
              onChange={(e) => setNouveauNom(e.target.value)}
              maxLength={10}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-base font-bold min-h-[44px]"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOngletActif('principal')}
                className="flex-1 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold min-h-[44px]"
              >
                Retour
              </button>
              <button
                type="submit"
                className="flex-1 py-2 rounded-lg bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 text-xs font-bold min-h-[44px]"
              >
                Renommer
              </button>
            </div>
          </form>
        )}

        {/* SOUS-MENU : BLOQUER / DÉBLOQUER */}
        {ongletActif === 'bloquer' && (
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">
              {table.bloquee ? 'Débloquer la table' : 'Bloquer la table (motif)'}
            </h4>
            {!table.bloquee ? (
              <>
                <label htmlFor="motif-blocage" className="block text-xs text-slate-600 dark:text-slate-400">
                  Motif du blocage (affiché à l’équipe) :
                </label>
                <input
                  id="motif-blocage"
                  type="text"
                  placeholder="Ex : Table réservée événement, pied cassé..."
                  value={motifBlocage}
                  onChange={(e) => setMotifBlocage(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm min-h-[44px]"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOngletActif('principal')}
                    className="flex-1 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold min-h-[44px]"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onBloquer(table.id, true, motifBlocage || 'Non précisé');
                      onFermer();
                    }}
                    className="flex-1 py-2 rounded-lg bg-amber-600 text-white text-xs font-bold min-h-[44px]"
                  >
                    Bloquer la table
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  Actuellement bloquée pour : <strong>{table.motifBlocage || 'Maintenance'}</strong>
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOngletActif('principal')}
                    className="flex-1 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold min-h-[44px]"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onBloquer(table.id, false);
                      onFermer();
                    }}
                    className="flex-1 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold min-h-[44px]"
                  >
                    Débloquer maintenant
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* SOUS-MENU : FUSION MANUELLE AVEC UNE TABLE VOISINE */}
        {ongletActif === 'fusion' && (
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">
              Coller une autre table à {table.nom} :
            </h4>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {tablesCandidatesFusion.map((cand) => (
                <button
                  key={cand.id}
                  type="button"
                  onClick={() => verifierEtFusionner([table.id, cand.id])}
                  className="w-full text-left p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60 flex items-center justify-between min-h-[44px] cursor-pointer"
                >
                  <span className="font-bold text-xs">
                    Table {cand.nom} ({cand.capaciteMax} pl., {cand.forme})
                  </span>
                  <span className="text-[11px] text-slate-500">
                    =&gt; Total {table.capaciteMax + cand.capaciteMax} pl.
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setOngletActif('principal')}
              className="w-full py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold min-h-[44px]"
            >
              Retour
            </button>
          </div>
        )}

        {/* SOUS-MENU : CONFIRMATION DE SUPPRESSION (NOMMANT LA TABLE) */}
        {ongletActif === 'confirmer_suppression' && (
          <div className="space-y-3 p-1" role="alertdialog" aria-labelledby="dialog-suppr-titre">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <h4 id="dialog-suppr-titre" className="font-bold text-sm">
                Supprimer la table {table.nom} ?
              </h4>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Cette action supprimera la table <strong>{table.nom}</strong> du plan. Les réservations
              qui y étaient associées devront être replacées.
            </p>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setOngletActif('principal')}
                className="flex-1 py-2.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold min-h-[44px] cursor-pointer"
              >
                Conserver la table
              </button>
              <button
                type="button"
                onClick={confirmerSuppression}
                className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold min-h-[44px] cursor-pointer shadow-xs active:scale-95"
              >
                Confirmer la suppression
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
